import React, { useRef } from 'react'
import { cn } from '../../../../../lib/utils'
import { motion, AnimatePresence } from 'framer-motion'
import animate from '../../../../styles/animations.module.css'
import { InfiniteScroll } from '../../../InfiniteScroll'

import {
  ActionMessage,
  ContentMessage,
  MessageComponent,
  TextMessage,
  type Attachment,
} from '../Message'
import { generateAvatar } from '../ChatWindow'
import ToolCall, { ToolCallComponent } from '../ToolCall'
import Icon, { type iconTypes } from '../../../Icon'

type ChatListComponent = MessageComponent | ToolCallComponent

export type ChatListProps = {
  id: string
  title?: string
  components: ChatListComponent[]
  newChatMessage: string
  isLoading: boolean
  img?: string
  handleLoadMoreMessages: () => void | Promise<void>
  scrollContainer: HTMLElement | null
  handleDownloadContent?: (contentUrl: string) => void | Promise<void>
}

export function ChatList({
  components,
  newChatMessage,
  img,
  isLoading,
  handleLoadMoreMessages,
  title,
  id,
  scrollContainer,
  handleDownloadContent,
}: ChatListProps) {
  const chatListRef = useRef<HTMLDivElement>(null)

  // If we have a scroll container, use it directly
  // If not, we'll use our own ref as a fallback
  const effectiveScrollContainer = scrollContainer || chatListRef.current

  const truncateFileName = (
    fileName: string,
    maxLength: number = 15,
  ): string => {
    if (fileName.length <= maxLength) return fileName
    const lastDotIndex = fileName.lastIndexOf('.')
    if (lastDotIndex === -1) {
      // No extension, just truncate
      return `${fileName.slice(0, maxLength - 3)}...`
    }
    const extension = fileName.slice(lastDotIndex + 1)
    const nameWithoutExt = fileName.slice(0, lastDotIndex)
    const truncatedName = nameWithoutExt.slice(
      0,
      maxLength - extension.length - 4,
    )
    return `${truncatedName}...${extension}`
  }

  const getAttachmentIcon = (
    mimeType: string | null | undefined,
  ): iconTypes => {
    if (!mimeType) return 'File'

    const mimeLower = mimeType.toLowerCase()

    // PDF
    if (mimeLower === 'application/pdf') {
      return 'FileText'
    }

    // HTML
    if (mimeLower === 'text/html' || mimeLower === 'application/xhtml+xml') {
      return 'FileCode'
    }

    // Images
    if (mimeLower.startsWith('image/')) {
      return 'Image'
    }

    // Audio
    if (mimeLower.startsWith('audio/')) {
      return 'AudioLines'
    }

    // Default
    return 'File'
  }

  const skeletonMessage = (position: 'left' | 'right' | 'top') => {
    return (
      <div
        className={cn('flex flex-col gap-2 p-4 whitespace-pre-wrap', {
          'items-end': position == 'right',
          'items-start': position == 'left',
        })}
      >
        <div className="flex gap-2 items-center">
          {position == 'left' &&
            generateAvatar({ name: '', img: '', isLoading: true })}
          <div
            className={cn(
              'flex flex-col min-w-[200px] w-4/5',
              animate.smallTransitionIn,
            )}
            title={`message-skeleton`}
          >
            <div className="w-full rounded-xl h-[50px] bg-accent animate-pulse" />
          </div>
          {position == 'right' &&
            generateAvatar({ name: '', img: '', isLoading: true })}
        </div>
      </div>
    )
  }

  const generateMessages = (components: ChatListComponent[]) => {
    if (isLoading) {
      return (
        <div className="flex flex-col justify-start h-full">
          {skeletonMessage('right')}
          {skeletonMessage('left')}
        </div>
      )
    }

    return (
      <>
        {components.map((component, index) => {
          const { type, props: subcomponent } = component
          return (
            <motion.div
              key={subcomponent.props.id}
              layout
              initial={{ opacity: 0, scale: 1, y: 50, x: 0 }}
              animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
              exit={{ opacity: 0, scale: 1, y: 1, x: 0 }}
              transition={{
                opacity: { duration: 0.1 },
                layout: {
                  type: 'spring',
                  bounce: 0.3,
                  duration: components.indexOf(component) * 0.05 + 0.2,
                },
              }}
              style={{
                originX: 0.5,
                originY: 0.5,
              }}
              className={cn('flex flex-col gap-2 p-4 whitespace-pre-wrap', {
                'items-end': type === 'message' && subcomponent.props?.isMine,
                'items-start':
                  type === 'message' && !subcomponent.props?.isMine,
                'items-start p-0': type === 'tool',
              })}
            >
              <div
                className={cn('flex gap-2 items-center', {
                  'justify-end':
                    type === 'message' && subcomponent.props?.isMine,
                  'justify-start': type === 'tool',
                })}
              >
                {type == 'message' &&
                  (subcomponent.type === 'text' ||
                    subcomponent.type === 'content') &&
                  !subcomponent.props?.isMine &&
                  generateAvatar({
                    name: subcomponent.props?.author?.name,
                    img: subcomponent.props?.author?.img,
                    icon: subcomponent.props?.author?.icon,
                    handleClick: subcomponent.props?.author?.handleClick,
                    href: subcomponent.props?.author?.href,
                    seed: subcomponent.props?.author?.seed,
                  })}
                {type == 'tool' && <ToolCall {...subcomponent} />}
                {type == 'message' && subcomponent.type === 'text' && (
                  <TextMessage {...subcomponent.props} title={title} />
                )}
                {type == 'message' && subcomponent.type === 'action' && (
                  <ActionMessage {...subcomponent.props} />
                )}
                {type == 'message' && subcomponent.type === 'content' && (
                  <ContentMessage {...subcomponent.props} title={title} />
                )}
                {type == 'message' &&
                  subcomponent.props?.isMine &&
                  generateAvatar({
                    name: subcomponent.props?.author?.name,
                    img: subcomponent.props?.author?.img,
                    icon: subcomponent.props?.author?.icon,
                    handleClick: subcomponent.props?.author?.handleClick,
                    href: subcomponent.props?.author?.href,
                    seed: subcomponent.props?.author?.seed,
                  })}
              </div>
              {type == 'message' &&
                subcomponent.props?.attachments &&
                subcomponent.props.attachments.length > 0 && (
                  <div
                    className={cn('flex flex-wrap gap-2 mt-2 pl-14', {
                      'justify-end': subcomponent.props?.isMine,
                      'justify-start': !subcomponent.props?.isMine,
                    })}
                  >
                    {subcomponent.props.attachments.map((attachment) => (
                      <button
                        key={attachment.id}
                        onClick={() => handleDownloadContent?.(attachment.id)}
                        className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-accent hover:bg-accent/80 text-foreground border border-border transition-colors"
                        title={attachment.name}
                      >
                        <Icon
                          name={getAttachmentIcon(attachment.mimeType)}
                          size={12}
                        />
                        {truncateFileName(attachment.name)}
                      </button>
                    ))}
                  </div>
                )}
              {type == 'message' &&
                subcomponent.props?.isFinalMessageOfChat &&
                index !== components.length - 1 && (
                  <div className="flex flex-col w-full text-2xs text-center text-primary/40 px-12 my-8">
                    {newChatMessage}
                    <hr />
                  </div>
                )}
            </motion.div>
          )
        })}
      </>
    )
  }

  return (
    <div
      className={cn(
        'w-full flex flex-col',
        // Only apply height and overflow when we're not using an external scroll container
        !scrollContainer && 'h-full overflow-y-auto',
      )}
      ref={chatListRef}
    >
      <InfiniteScroll
        id={id}
        direction="up"
        scrollContainer={effectiveScrollContainer}
        handleLoadMoreItems={handleLoadMoreMessages}
        threshold={1}
      >
        <AnimatePresence>
          {components && generateMessages(components)}
        </AnimatePresence>
      </InfiniteScroll>
    </div>
  )
}
